import React, { useState } from 'react';
import {
  Body,
  Title,
  Loader,
  Flex,
  Button,
  Dropdown,
  Menu,
  Icon,
  SegmentedControl,
} from '@cognite/cogs.js';
import {
  DigitalTwin,
  Milestone,
  RuleRunStatus,
  useGetCollectionReportsQuery,
} from 'types/dt-graphql-apollo-v2';
import styled from 'styled-components/macro';
import { InfoMessage } from 'components/InfoMessage/InfoMessage';
import { useStatefulHistory } from 'hooks/history';
import { useUrl } from 'hooks/useUrls';
import { getCollectionRunUrl } from 'utils/router-url';
import { sortRunsByDate } from 'utils/data-validation';
import { CollectionCard } from './components/CollectionCard';
import { ConsistencyReports } from './components/ConsistencyReports';
import { Collection, sortCollections, sortOptions } from './helpers';

enum ReportType {
  Validation = 'validation',
  Consistency = 'consistency',
}

const isReportType = (key: string): key is ReportType =>
  key === ReportType.Validation || key === ReportType.Consistency;

export const DataQualityExplorer = ({
  digitalTwin,
}: {
  digitalTwin: DigitalTwin;
}) => {
  const history = useStatefulHistory();
  const tenant = useUrl.Tenant();

  const [reportType, setReportType] = useState<ReportType>(
    ReportType.Validation
  );
  const [sortOption, setSortOption] = useState(sortOptions[0].value);

  const {
    data: collectionsData,
    loading: collectionsLoading,
    error: collectionsError,
  } = useGetCollectionReportsQuery({
    variables: {
      projectId: digitalTwin.externalId,
    },
  });

  const collections = collectionsData?.listMilestone?.items as Milestone[];

  const sortingDisabled = collections?.length === 0;
  const selectedSortOption = sortOptions.find(
    (option) => option.value === sortOption
  )?.name;

  const collectionsRuns: Collection[] = collections
    ?.map((collection) => {
      const validRules =
        collection.rules?.items.filter(
          (rule) => rule!.ruleRunStatus!.items.length > 0
        ) || [];
      const sortedRuns = sortRunsByDate(
        (validRules![0]?.ruleRunStatus!.items as RuleRunStatus[]) || []
      );
      const collectionReportId = sortedRuns[0]?.reportId || '';

      return { ...collection, reportId: collectionReportId };
    })
    .filter((c) => Boolean(c.reportId));

  const renderValidationContent = () => {
    if (collectionsError?.message) {
      return (
        <InfoMessage
          type="Error"
          title="Something went wrong while fetching the collections"
        >
          {collectionsError.message}
        </InfoMessage>
      );
    }
    if (collectionsLoading) {
      return <Loader />;
    }

    if (collectionsRuns.length === 0) {
      return (
        <InfoMessage type="Search" title="Data Quality Explorer is empty">
          No collection reports were found.
        </InfoMessage>
      );
    }

    const sortedCollections = sortCollections(sortOption, collectionsRuns);

    return (
      <>
        <SortRow alignItems="flex-end" direction="column">
          <Dropdown
            content={
              <Menu>
                {sortOptions.map((option) => (
                  <Menu.Item
                    key={option.value}
                    onClick={() => setSortOption(option.value)}
                  >
                    <Flex
                      alignItems="center"
                      gap={8}
                      justifyContent="space-between"
                    >
                      <span>{option.name}</span>
                      <Icon type={option.icon} />
                    </Flex>
                  </Menu.Item>
                ))}
              </Menu>
            }
            disabled={sortingDisabled}
          >
            <Button
              disabled={sortingDisabled}
              icon="ReorderDefault"
              iconPlacement="right"
            >
              Sort
            </Button>
          </Dropdown>
          <Body size="small">{selectedSortOption}</Body>
        </SortRow>

        <StyledList wrap="wrap" gap={42}>
          {sortedCollections.map(({ reportId, ...collection }) => (
            <CollectionCard
              key={collection.externalId}
              collection={collection}
              allCollections={sortedCollections}
              onOpen={() =>
                history.pushStateful(
                  getCollectionRunUrl(tenant, digitalTwin.externalId, reportId)
                )
              }
            />
          ))}
        </StyledList>
      </>
    );
  };

  return (
    <StylePage direction="column">
      <StyledHeader>
        <div>
          <Title level={3}>Data quality explorer</Title>
          <Body size="small">
            Explore the current status of the data quality for each collection.
          </Body>
        </div>

        <SegmentedControl
          currentKey={reportType}
          onButtonClicked={(key) => {
            if (isReportType(key)) {
              setReportType(key);
            }
          }}
        >
          <SegmentedControl.Button key={ReportType.Validation}>
            Validation Reports
          </SegmentedControl.Button>
          <SegmentedControl.Button key={ReportType.Consistency}>
            Consistency Reports
          </SegmentedControl.Button>
        </SegmentedControl>
      </StyledHeader>

      {reportType === ReportType.Validation && renderValidationContent()}
      {reportType === ReportType.Consistency && (
        <ConsistencyReports digitalTwin={digitalTwin} />
      )}
    </StylePage>
  );
};

const StylePage = styled(Flex)`
  flex: 1;
  overflow: hidden;
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin: 2rem 0;
  padding: 0 2rem;
  gap: 1rem;

  .cogs-body--small {
    color: var(--cogs-text-color-secondary);
  }
`;

const SortRow = styled(Flex)`
  padding: 0 2rem;

  .cogs-body--small {
    color: var(--cogs-text-color-secondary);
  }
`;

const StyledList = styled(Flex)`
  overflow: auto;
  padding: 1rem 2rem;
  margin-bottom: 2rem;
`;
